import * as THREE from 'three';
import FlightModel from './FlightModel.js';
import EngineState from './EngineState.js';
import CountermeasureDispenser from './CountermeasureDispenser.js';
import HitVolume from '../weapons/HitVolume.js';
import { ENV, AIRCRAFT, GROUND, CAMERA } from '../core/SimConfig.js';
import { DEG2RAD, rand, srand } from '../utils.js';

const _force = new THREE.Vector3();
const _torque = new THREE.Vector3();
const _acc = new THREE.Vector3();
const _angAcc = new THREE.Vector3();
const _torqueLocal = new THREE.Vector3();
const _invQuat = new THREE.Quaternion();
const _deltaQuat = new THREE.Quaternion();
const _omegaAxis = new THREE.Vector3();
const _grav = new THREE.Vector3();
const _r = new THREE.Vector3();
const _rCrossF = new THREE.Vector3();
const _zeroWind = new THREE.Vector3();
const _euler = new THREE.Euler();
const _hitLocal = new THREE.Vector3();
const _yawTorque = new THREE.Vector3();
const _emitterWorld = new THREE.Vector3();
const _aoaVel = new THREE.Vector3();
const _aoaCross = new THREE.Vector3();

export default class Aircraft {
  constructor({ model, params, startEngineOn = true }) {
    this.group = new THREE.Group();

    this.group.add(model);

    // Cockpit LOD nodes (set by name from the GLB)
    this.cockpitHD = model.getObjectByName('_Cockpit_HD') || null;
    this.cockpitSD = model.getObjectByName('_Cockpit_SD') || null;
    // The seated pilot (body, with his head parented to it). A top-level node
    // rather than a child of the SD shell, so the in-cockpit action cameras can
    // frame him with the HD interior up; App drives his visibility off the
    // active camera's dressing.
    this.pilot = model.getObjectByName('_Cockpit_Pilot') || null;

    this.flightModel = new FlightModel(params);

    // Physics state
    this.position = new THREE.Vector3(0, 0, 0);
    this.velocity = new THREE.Vector3(0, 0, 0);
    this.quaternion = new THREE.Quaternion(); // identity = forward toward -Z
    this.angularVelocity = new THREE.Vector3(0, 0, 0);

    // Cached body-basis vectors. The forward/right/up getters are read many
    // times per frame (Controller, GroundContact, cameras, HUD); computing them
    // from the quaternion on each access used to allocate a fresh Vector3 every
    // time. Instead we recompute lazily only when the quaternion actually
    // changes (tracked via its components) and hand back the shared instances.
    this._basis = {
      forward: new THREE.Vector3(0, 0, -1),
      right: new THREE.Vector3(1, 0, 0),
      up: new THREE.Vector3(0, 1, 0),
      qx: NaN, qy: NaN, qz: NaN, qw: NaN,
    };

    // Control inputs
    this.throttle = 0;
    this.elevator = 0;
    this.ailerons = 0;
    this.rudder = 0;
    // Multiplier on the airframe's roll authority. 1 for manual flight; the
    // Basic scheme sets it below 1 to fly at the roll power its look-to-steer
    // autopilot was tuned for (AUTOPILOT.rollAuthority).
    this.rollAuthorityScale = 1;

    // Engine state machine (startup / shutdown sequences)
    this.engineState = new EngineState(startEngineOn);

    // Countermeasure flare dispenser — flareSystem attached later by App.
    this.countermeasures = new CountermeasureDispenser({
      model,
      aircraft: this,
      flareSystem: null,
    });

    // Engine RPM — lags behind throttle with spool-up/spool-down delay
    const rpm = params.engineRPM || AIRCRAFT.engineRPM;
    this._rpmIdle = rpm.idle;
    this._rpmMax = rpm.max;
    this._rpmSpoolTime = rpm.spoolTime;
    this.engineRPM = this.engineState.isRunning ? rpm.idle : 0;

    // Flaps: stage (0/1/2) sets the target, flaps (0–1) interpolates linearly
    this.flapStage = 0;
    this.flaps = 0;
    this.flapRate = 0.5; // units per second (full deploy in 2s)

    // Airbrake: input is 0/1 (key held), airbrake smooths toward it
    this.airbrakeInput = 0;
    this.airbrake = 0;
    this.airbrakeSmoothing = 5; // per-second lerp speed

    // Wheel brake: same B key input, separate state for ground braking
    this.wheelBrake = 0;

    // Landing gear: target is 0/1 (toggled by key), gear interpolates linearly
    this.gearTarget = 1; // start with gear down
    this.gear = 1;
    this.gearRate = 0.2; // units per second (full cycle ≈ 2.5s)

    // Params
    this.mass = params.mass;
    this.momentOfInertia = params.momentOfInertia;

    // Body-local G-force (for camera effects)
    this.gForceLocal = new THREE.Vector3(0, 1, 0);

    // External force/torque accumulators (populated by GroundContact before update)
    this._extForce = new THREE.Vector3();
    this._extTorque = new THREE.Vector3();

    // Ground mode state (set by GroundContact each frame)
    this.onGround = false;
    this.bellyContact = false;
    this.blendToFlight = 1;
    // Per-wheel ground state, also written by GroundContact each frame from the
    // contact raycasts. Read by LandingGearAnimator to roll each tyre, steer the
    // nose gear and compress the suspension — per-wheel rather than the
    // airframe-wide flag so a main-gear-first touchdown spins the mains up while
    // the nose wheel is still free. groundY is the collision surface under that
    // leg's ray, -Infinity when the gear is stowed and no ray was cast.
    this.wheelGround = {
      nose:  { onGround: false, groundY: -Infinity },
      left:  { onGround: false, groundY: -Infinity },
      right: { onGround: false, groundY: -Infinity },
    };

    // Terrain elevation directly below the aircraft origin + height above it,
    // cached each frame from the safety-clamp query in update() so other systems
    // (e.g. the Voice Warning System) can read AGL without a second terrain probe.
    this.groundHeight = 0;
    this.altitudeAGL = Infinity;

    // Collision volume — lets enemy missiles run swept hit-tests against the
    // A-10. The model needs a "Hitbox" node (empty or mesh) for this to be
    // available; if absent, missiles fall back to their internal proximity
    // fuse for detonation triggering.
    const hitboxNode = model.getObjectByName('Hitbox');
    if (hitboxNode) {
      if (hitboxNode.isMesh) hitboxNode.visible = false;
      this.hitVolume = new HitVolume(hitboxNode);
    } else {
      this.hitVolume = null;
    }

    // ── Battle damage ─────────────────────────────────────────────────────
    // Enemy missile strikes knock out the engine on the struck side. Each
    // dead engine streams fire/smoke from its emitter anchor (set by App) and
    // drops the throttle that drives both thrust and engine audio.
    this.engineLeftOut = false;
    this.engineRightOut = false;
    // Count of enemy missile strikes survived. The first two knock out the
    // engines (one side each); the third triggers total destruction.
    this.missileStrikes = 0;
    this.particleSystem = null;                                  // attached later by App
    this.emitterLeft = model.getObjectByName('Emitter_Left') || null;
    this.emitterRight = model.getObjectByName('Emitter_Right') || null;
    // Camera impact jolt: set to 1 on a missile strike, decayed each frame.
    // Read by CockpitCamera for a quick shake (mirrors gForceLocal — physics
    // state exposed purely for camera effects).
    this.impactTrauma = 0;
    // Camera launch turbulence: set to 1 when a missile leaves the rail
    // (WeaponSystem), decayed each frame. Read by CockpitCamera for a brief,
    // subtle rocket-firing shake — far gentler than impactTrauma.
    this.launchTrauma = 0;

    // ── Total destruction ─────────────────────────────────────────────────
    // Latched when a ground impact exceeds the crash criteria (high closing
    // speed, or an extreme nose/tail/wing attitude — see _evaluateCrash). Once
    // set, the wreck loses thrust + control; App reacts to the flag to spawn the
    // explosion, silence A-10 audio, force an external view, and show the
    // "AIRCRAFT DESTROYED" overlay.
    this.destroyed = false;
    this._wasGroundContact = false;
    // Latched on the frame a wreck that was destroyed in the air first slams
    // into the terrain. App reads it (one-shot) to spawn a ground-impact
    // explosion — a wreck killed by a belly crash already gets its blast from
    // the `destroyed` reaction, so this only fires for an airborne kill.
    this.terrainImpact = false;
    // Random-interval throttle for the grounded heat plume (see update step 8).
    this._groundFireTimer = 0;
    this._dustTimer = 0;
  }

  /**
   * Enemy-missile detonation against the A-10. Determines the struck side from
   * the hit point in body-local space and kills that engine (the opposite one
   * if it's already out), so two hits take both engines regardless of side. A
   * third strike (both engines already out) latches total destruction.
   * The detonation's audio (interior boom + master caution) is driven by App
   * from the drained explosion event.
   */
  onProjectileHit(hx, hy, hz, _damage = 0, _source = null) {
    this.impactTrauma = 1; // kick the cockpit impact shake
    if (this.destroyed) return;
    this.missileStrikes++;
    if (this.missileStrikes >= 3) {
      this._destroy();
      return;
    }
    _invQuat.copy(this.quaternion).invert();
    _hitLocal.set(hx, hy, hz).sub(this.position).applyQuaternion(_invQuat);
    const struckLeft = _hitLocal.x < 0; // body +x = right
    if (struckLeft) {
      if (this.engineLeftOut) this.engineRightOut = true;
      else this.engineLeftOut = true;
    } else {
      if (this.engineRightOut) this.engineLeftOut = true;
      else this.engineRightOut = true;
    }
  }

  /**
   * Apply a force at a world-space point (not the CG).
   * Accumulates both the linear force and the torque r × F.
   * Call before update(); accumulators are cleared at end of update().
   */
  applyForceAtPoint(force, worldPoint) {
    this._extForce.add(force);
    _r.subVectors(worldPoint, this.position);
    _rCrossF.copy(_r).cross(force);
    this._extTorque.add(_rCrossF);
  }

  /**
   * Sync scene graph so the group reflects the current physics state.
   * Must be called before any system that reads gear mesh world matrices
   * (animators, GroundContact, ForceViz).
   */
  syncSceneGraph() {
    this.group.position.copy(this.position);
    this.group.quaternion.copy(this.quaternion);
  }

  update(dt, groundContact = null, wind = null, terrainCollider = null) {
    // Latch total destruction on a hard / improper ground impact before any
    // integration runs — velocity still holds the incoming impact speed here.
    this._evaluateCrash(terrainCollider);

    // 0. Interpolate flaps linearly toward target
    const flapTargets = [0, 0.5, 1];
    const flapTarget = flapTargets[this.flapStage];
    const flapDelta = flapTarget - this.flaps;
    if (Math.abs(flapDelta) > 0.001) {
      this.flaps += Math.sign(flapDelta) * Math.min(this.flapRate * dt, Math.abs(flapDelta));
    } else {
      this.flaps = flapTarget;
    }

    // 0b. Smooth airbrake toward input (disabled below minSpeed)
    const airbrakeTarget = (this.airspeed >= AIRCRAFT.airbrakeParams.minSpeed) ? this.airbrakeInput : 0;
    this.airbrake += (airbrakeTarget - this.airbrake) * (1 - Math.exp(-this.airbrakeSmoothing * dt));

    // 0b2. Smooth wheel brake toward input (active on ground only)
    const wheelBrakeTarget = this.onGround ? this.airbrakeInput : 0;
    this.wheelBrake += (wheelBrakeTarget - this.wheelBrake) * (1 - Math.exp(-GROUND.brakeSmoothing * dt));

    // 0c. Interpolate landing gear linearly toward target
    const gearDelta = this.gearTarget - this.gear;
    if (Math.abs(gearDelta) > 0.001) {
      this.gear += Math.sign(gearDelta) * Math.min(this.gearRate * dt, Math.abs(gearDelta));
    } else {
      this.gear = this.gearTarget;
    }

    // 0d. Engine state machine tick
    this.engineState.update(dt);

    // 0d2. Countermeasure dispenser tick (drives FlareSystem.dispense)
    this.countermeasures.update(dt);

    // 0d3. Decay the missile-impact camera jolt (linear → fully ends).
    if (this.impactTrauma > 0) {
      this.impactTrauma = Math.max(0, this.impactTrauma - AIRCRAFT.engineDamage.impactShakeDecay * dt);
    }
    if (this.launchTrauma > 0) {
      this.launchTrauma = Math.max(0, this.launchTrauma - AIRCRAFT.engineDamage.launchShakeDecay * dt);
    }

    // 0e. Spool engine RPM based on engine state
    // Battle damage scales the commanded throttle by the number of live engines
    // (1 → 0.5 → 0). Folding it into the RPM target — rather than the thrust —
    // lets the engine audio wind down naturally with no special damaged voices.
    const enginesAlive = (this.engineLeftOut ? 0 : 1) + (this.engineRightOut ? 0 : 1);
    const effectiveThrottle = this.throttle * (enginesAlive * 0.5);
    if (this.engineState.isRunning) {
      // Normal operation — chase throttle target
      const targetRPM = this._rpmIdle + effectiveThrottle * (this._rpmMax - this._rpmIdle);
      this.engineRPM += (targetRPM - this.engineRPM) * (1 - Math.exp(-dt / this._rpmSpoolTime));
    } else if (this.engineState.isStarting) {
      // Ramp 0 → idle over startup duration
      this.engineRPM = this._rpmIdle * this.engineState.transitionProgress;
    } else if (this.engineState.isStopping) {
      // Ramp current → 0 over shutdown duration
      // Capture RPM at start of shutdown on first frame (progress ≈ 0)
      if (!this._shutdownStartRPM) this._shutdownStartRPM = this.engineRPM;
      this.engineRPM = this._shutdownStartRPM * (1 - this.engineState.transitionProgress);
    } else {
      // OFF
      this.engineRPM = 0;
      this._shutdownStartRPM = 0;
    }

    // Clear shutdown snapshot when not stopping
    if (!this.engineState.isStopping) this._shutdownStartRPM = 0;

    // Thrust factor: 0 at idle RPM, 1 at max RPM (clamped — 0 when below idle)
    // Exposed on the instance so GroundContact can use the same value
    const thrustFactor = Math.max(0, (this.engineRPM - this._rpmIdle) / (this._rpmMax - this._rpmIdle));
    this.thrustFactor = thrustFactor;

    // 0f. Asymmetric-thrust yaw — a single live engine pushes off the centreline
    // and yaws the nose toward the dead one. Scales with the live engine's
    // thrust; added into the external-torque accumulator consumed below.
    if (enginesAlive === 1) {
      const moment = this.flightModel.maxThrust * thrustFactor * AIRCRAFT.engineDamage.thrustArm;
      const dir = this.engineLeftOut ? 1 : -1; // body +y yaws nose left (toward a dead left engine)
      _yawTorque.set(0, dir * moment, 0).applyQuaternion(this.quaternion);
      this._extTorque.add(_yawTorque);
    }

    // 1. Compute forces and torques
    const result = this.flightModel.computeForces(
      {
        position: this.position,
        velocity: this.velocity,
        quaternion: this.quaternion,
        angularVelocity: this.angularVelocity,
      },
      {
        throttle: thrustFactor,
        elevator: this.elevator,
        ailerons: this.ailerons,
        rudder: this.rudder,
        rollAuthorityScale: this.rollAuthorityScale,
        flaps: this.flaps,
        airbrake: this.airbrake,
        gear: this.gear,
      },
      wind || _zeroWind,
    );
    _force.copy(result.force).add(this._extForce);
    _torque.copy(result.torque).add(this._extTorque);

    // ── Ground mode force blending ──────────────────────────────────────
    // Lerp between flight forces (incl. springs) and simplified ground forces
    if (groundContact && this.blendToFlight < 1 && this.onGround) {
      const blend = this.blendToFlight;
      _force.multiplyScalar(blend).addScaledVector(groundContact.groundForce, 1 - blend);
      _torque.multiplyScalar(blend).addScaledVector(groundContact.groundTorque, 1 - blend);
    }

    this.lastForces = result.forces;
    this.alpha = result.alpha || 0;
    this.stallAngle = result.stallAngle || (15 * DEG2RAD);

    // 2. Linear integration (semi-implicit Euler)
    _acc.copy(_force).divideScalar(this.mass);

    // Body-local G-force: (acceleration - gravity) in body frame, in G units
    _grav.set(0, -ENV.gravity, 0);
    _invQuat.copy(this.quaternion).invert();
    this.gForceLocal.copy(_acc).sub(_grav).divideScalar(ENV.gravity).applyQuaternion(_invQuat);

    this.velocity.addScaledVector(_acc, dt);
    this.position.addScaledVector(this.velocity, dt);

    // 3. Angular integration
    // Convert torque to body-local, divide by MoI per axis, convert back to world
    _invQuat.copy(this.quaternion).invert();
    _torqueLocal.copy(_torque).applyQuaternion(_invQuat);
    _angAcc.set(
      _torqueLocal.x / this.momentOfInertia.pitch,
      _torqueLocal.y / this.momentOfInertia.yaw,
      _torqueLocal.z / this.momentOfInertia.roll,
    );
    _angAcc.applyQuaternion(this.quaternion);
    this.angularVelocity.addScaledVector(_angAcc, dt);

    // Delta quaternion from angularVelocity * dt
    const omegaMag = this.angularVelocity.length();
    if (omegaMag > 0.0001) {
      _omegaAxis.copy(this.angularVelocity).divideScalar(omegaMag);
      const halfAngle = omegaMag * dt * 0.5;
      _deltaQuat.set(
        _omegaAxis.x * Math.sin(halfAngle),
        _omegaAxis.y * Math.sin(halfAngle),
        _omegaAxis.z * Math.sin(halfAngle),
        Math.cos(halfAngle),
      );
      this.quaternion.premultiply(_deltaQuat).normalize();
    }

    // ── Ground mode kinematic constraints ───────────────────────────────
    // After integration, correct position/orientation toward rest state.
    // Uses time-based exponential decay so constraints are soft enough to
    // allow rotation for takeoff while still leveling the aircraft at rest.
    if (groundContact && this.blendToFlight < 1 && this.onGround) {
      const correction = 1 - this.blendToFlight;

      // Pin Y to rest height (no spring oscillation)
      this.position.y += (groundContact.restHeight - this.position.y) * correction;

      // Damp vertical velocity
      this.velocity.y *= this.blendToFlight;

      // Level roll and pitch toward rest (time-based, not per-frame snap)
      // Pitch targets brake-dip angle instead of zero when braking
      const levelRate = 5; // per second — settles in ~0.5s at full ground mode
      const levelFactor = 1 - Math.exp(-levelRate * dt * correction);
      const pitchTarget = groundContact.brakePitch || 0;
      _euler.setFromQuaternion(this.quaternion, 'YXZ');
      _euler.x += (pitchTarget - _euler.x) * levelFactor;
      _euler.z *= (1 - levelFactor);
      this.quaternion.setFromEuler(_euler);

      // Damp pitch/roll angular velocity (time-based)
      const angDampRate = 8; // per second
      const angDampFactor = Math.exp(-angDampRate * dt * correction);
      _invQuat.copy(this.quaternion).invert();
      _torqueLocal.copy(this.angularVelocity).applyQuaternion(_invQuat);
      _torqueLocal.x *= angDampFactor; // pitch rate
      _torqueLocal.z *= angDampFactor; // roll rate
      this.angularVelocity.copy(_torqueLocal).applyQuaternion(this.quaternion);

      // Lateral velocity is handled by force-based friction in GroundContact,
      // not kinematic constraint — allows smooth yaw steering during transition.
    }

    // 4. Safety clamp — prevent falling through the world (crash recovery)
    const safetyGroundY = terrainCollider
      ? (terrainCollider.getHeightAt(this.position.x, this.position.z) ?? 0)
      : 0;
    // Cache AGL for downstream consumers (Voice Warning System) — reuses this
    // query so no system needs a second terrain probe.
    this.groundHeight = safetyGroundY;
    this.altitudeAGL = terrainCollider ? (this.position.y - safetyGroundY) : Infinity;
    if (this.position.y < safetyGroundY - 2) {
      // The CG has punched well below the surface — e.g. a belly slide into
      // rising terrain that the capped contact spring can't keep up with. This
      // bypasses the rising-edge crash check in _evaluateCrash (contact is
      // already latched), so judge the impact here on the still-intact velocity
      // before we correct it: a hard/improper hit destroys the airframe.
      if (!this.destroyed && this._crashCriteriaMet(terrainCollider)) this._destroy();

      // Recover to the surface. Kill only the downward component so a survivable
      // scrape keeps its horizontal momentum and slides to a stop instead of
      // dead-stopping the instant it clips a bump.
      this.position.y = safetyGroundY;
      if (this.velocity.y < 0) this.velocity.y = 0;
    }

    // 5. Clear external force accumulators (used by GroundContact)
    this._extForce.set(0, 0, 0);
    this._extTorque.set(0, 0, 0);

    // 6. Final scene graph sync (captures integration results for rendering)
    this.group.position.copy(this.position);
    this.group.quaternion.copy(this.quaternion);

    // 7. Refresh hit-volume OBB so projectile sweep tests see the new pose.
    if (this.hitVolume) this.hitVolume.update();

    // 8. Battle-damage fire/smoke. In the air, each dead engine streams fire +
    // black smoke from its emitter anchor (a downed MIG's worst damage stage).
    // At/near the ground the emitter trails are replaced by a single buoyant
    // heat plume rising from the aircraft origin — a wreck burning where it
    // sits. Keyed off AGL (not onGround) so a wreck resting in any attitude —
    // rolled, nose-down, gear-up — still reads as grounded even when the
    // wheel/belly contact test doesn't register.
    if (this.particleSystem && (this.engineLeftOut || this.engineRightOut)) {
      const v = this.velocity;
      const ed = AIRCRAFT.engineDamage;
      const nearGround = this.onGround
        || (isFinite(this.altitudeAGL) && this.altitudeAGL < ed.groundFireAGL);
      if (nearGround) {
        // Throttled to a random interval (like the destroyed BTR) so the wreck's
        // plume doesn't flood the particle pool every frame.
        this._groundFireTimer -= dt;
        if (this._groundFireTimer <= 0) {
          this._groundFireTimer = rand(ed.groundFireInterval.min, ed.groundFireInterval.max);
          this.particleSystem.spawnGroundFireSmokeTrail(this.position.x, this.position.y, this.position.z, v.x, v.y, v.z);
        }
      } else {
        if (this.engineLeftOut && this.emitterLeft) {
          this.emitterLeft.getWorldPosition(_emitterWorld);
          this.particleSystem.spawnFireSmokeTrail(_emitterWorld.x, _emitterWorld.y, _emitterWorld.z, v.x, v.y, v.z);
        }
        if (this.engineRightOut && this.emitterRight) {
          this.emitterRight.getWorldPosition(_emitterWorld);
          this.particleSystem.spawnFireSmokeTrail(_emitterWorld.x, _emitterWorld.y, _emitterWorld.z, v.x, v.y, v.z);
        }
      }
    }

    // 9. Ground-scrape dust. Kicked up while the airframe drags along the
    // ground — a gear-up belly slide, or a destroyed wreck grinding to a halt.
    // Throttled and speed-gated so a stationary wreck stops pluming.
    if (this.particleSystem && (this.bellyContact || (this.destroyed && this.onGround))) {
      const gd = AIRCRAFT.groundDust;
      const groundSpeed = Math.hypot(this.velocity.x, this.velocity.z);
      if (groundSpeed >= gd.minSpeed) {
        this._dustTimer -= dt;
        if (this._dustTimer <= 0) {
          this._dustTimer = rand(gd.interval.min, gd.interval.max);
          // Spawn under the origin with XZ scatter; trail behind at a fraction
          // of ground speed plus a small upward kick.
          this.particleSystem._emit('dustLarge',
            this.position.x + srand() * gd.scatter,
            this.groundHeight,
            this.position.z + srand() * gd.scatter,
            -this.velocity.x * 0.3 + srand() * 0.5,
            rand(0.5, 2),
            -this.velocity.z * 0.3 + srand() * 0.5,
          );
        }
      }
    }
  }

  /**
   * Latch "total destruction" on the frame the airframe first slams into the
   * ground beyond survivable-landing limits. Evaluated only on the rising edge
   * of ground contact, using the still-uncorrected incoming velocity, so a
   * gentle gear or belly landing (low closing speed, wings level, mild pitch) is
   * spared while a high-speed impact or a nose/tail/wing strike destroys the
   * aircraft. App reacts to the latched `destroyed` flag.
   *
   * A wreck already destroyed in the air (combat kill) isn't re-destroyed here,
   * but its first ground contact latches `terrainImpact` so App can spawn a
   * ground-impact explosion as the tumbling wreck slams down.
   */
  _evaluateCrash(terrainCollider) {
    // "In contact" = wheel/belly ground contact OR the origin dropping below the
    // terrain surface. The latter catches a fast, steep dive that punches
    // through before GroundContact's spring registers (and before the safety
    // clamp below would zero the velocity, hiding the true impact speed).
    const groundY = terrainCollider ? terrainCollider.getHeightAt(this.position.x, this.position.z) : null;
    const belowTerrain = groundY != null && this.position.y < groundY;
    const contact = this.onGround || belowTerrain;

    if (contact && !this._wasGroundContact) {
      if (!this.destroyed) {
        // Healthy airframe hitting the ground beyond survivable limits.
        if (this._crashCriteriaMet(terrainCollider)) this._destroy();
      } else if (!this.terrainImpact) {
        // Already a wreck (killed in the air) touching down for the first time.
        this.terrainImpact = true;
      }
    }
    this._wasGroundContact = contact;
  }

  /**
   * Judge whether the current velocity/attitude relative to the terrain exceeds
   * survivable-landing limits. Reads the live velocity (caller must invoke this
   * before any correction zeroes it), so it works both on the rising edge of
   * contact and on a deep mid-slide penetration (see the safety clamp in
   * update()). Returns true when the airframe should be destroyed.
   *
   * @param {import('../world/TerrainCollider.js').default|null} terrainCollider
   * @returns {boolean}
   */
  _crashCriteriaMet(terrainCollider) {
    const C = AIRCRAFT.crashDamage;

    // Surface normal at the impact point (flat up-vector fallback).
    let nx = 0, ny = 1, nz = 0;
    const n = terrainCollider?.getNormalAt(this.position.x, this.position.z);
    if (n) { nx = n.x; ny = n.y; nz = n.z; }

    // Closing speed into the terrain (positive = driving into the surface).
    const vClose = -(this.velocity.x * nx + this.velocity.y * ny + this.velocity.z * nz);

    // Attitude at touchdown.
    const fwd = this.forwardVector;
    const right = this.rightVector;
    const up = this.upVector;
    const pitch = Math.asin(Math.max(-1, Math.min(1, fwd.y))); // +ve = nose up
    const bank = Math.atan2(right.y, up.y);                    // signed roll

    return vClose > C.maxImpactSpeed
      || Math.abs(bank) > C.maxBankAngle
      || pitch < C.minPitchAngle
      || pitch > C.maxPitchAngle;
  }

  /**
   * Apply total destruction: kill both engines (drops thrust and winds the
   * engine audio down) and jam the control surfaces at fixed random deflections
   * so the burning wreck tumbles out of control. The Controller stops driving
   * the surfaces once `destroyed` latches, so these held values persist and the
   * residual airflow torques send the airframe pitching/rolling/yawing wildly.
   * The cosmetic explosion, audio silencing, camera switch, and overlay are
   * orchestrated by App off the `destroyed` flag.
   */
  _destroy() {
    this.destroyed = true;
    this.engineLeftOut = true;
    this.engineRightOut = true;
    this.throttle = 0;
    // Jam each surface at a strong (magnitude 0.5–1) random deflection with a
    // random sign — guarantees a noticeable, chaotic tumble rather than a clean
    // ballistic arc.
    const jam = () => (Math.random() < 0.5 ? -1 : 1) * rand(0.5, 1);
    this.elevator = jam();
    this.ailerons = jam();
    this.rudder = jam();
    this.countermeasures.firing = false;
  }

  toggleEngine() {
    // Block shutdown while airborne — only allow toggling on the ground.
    if (this.engineState.isRunning && !this.isGrounded) return;
    this.engineState.toggle();
  }

  /**
   * Force the engine to the running state at idle RPM, skipping the startup
   * sequence. Used when promoting a cold-and-dark preview into an
   * engine-running scenario without rebuilding the aircraft.
   */
  setEngineRunning() {
    this.engineState = new EngineState(true);
    this.engineRPM = this._rpmIdle;
  }

  /** Normalized RPM: 0 = idle, 1 = max. Used by AudioManager. */
  get normalizedRPM() {
    return Math.max(0, Math.min(1,
      (this.engineRPM - this._rpmIdle) / (this._rpmMax - this._rpmIdle)));
  }

  get airspeed() {
    return this.velocity.length();
  }

  get altitude() {
    return this.position.y;
  }

  /**
   * Recompute the cached body-basis vectors if the quaternion has changed since
   * the last refresh. Returned vectors are shared instances — callers must treat
   * them as read-only (clone/copy before mutating).
   */
  _refreshBasis() {
    const q = this.quaternion;
    const b = this._basis;
    if (q.x === b.qx && q.y === b.qy && q.z === b.qz && q.w === b.qw) return;
    b.forward.set(0, 0, -1).applyQuaternion(q);
    b.right.set(1, 0, 0).applyQuaternion(q);
    b.up.set(0, 1, 0).applyQuaternion(q);
    b.qx = q.x; b.qy = q.y; b.qz = q.z; b.qw = q.w;
  }

  get forwardVector() {
    this._refreshBasis();
    return this._basis.forward;
  }

  get rightVector() {
    this._refreshBasis();
    return this._basis.right;
  }

  get upVector() {
    this._refreshBasis();
    return this._basis.up;
  }

  get angleOfAttack() {
    // Below ~2 m/s the velocity direction is dominated by ground-contact
    // spring jitter; normalizing that noise produces a wildly-swinging AoA.
    if (this.velocity.lengthSq() < 4) return 0;
    this._refreshBasis();
    const fwd = this._basis.forward;
    _aoaVel.copy(this.velocity).normalize();
    const dot = fwd.dot(_aoaVel);
    _aoaCross.crossVectors(fwd, _aoaVel);
    const sign = Math.sign(_aoaCross.dot(this._basis.right));
    return sign * Math.acos(Math.min(1, Math.max(-1, dot)));
  }

  get verticalSpeed() {
    return this.velocity.y;
  }

  /**
   * High-AoA airframe buffet, 0..1 — ramps in as the wing approaches the stall
   * (CAMERA.cockpit.buffetShake.aoaStart → aoaFull, as fractions of the stall
   * angle). One definition shared by the cockpit shake and the buffet rumble so
   * the two always start together. In flight only, and suppressed below
   * minSpeed: alpha is derived from velocity direction and goes noisy near zero
   * speed, so parked/taxi/ground-roll AoA is meaningless.
   */
  get buffetIntensity() {
    const b = CAMERA.cockpit.buffetShake;
    if (this.onGround || this.airspeed < b.minSpeed) return 0;
    const aoaRatio = Math.abs(this.alpha) / (this.stallAngle || 15 * DEG2RAD);
    return Math.max(0, Math.min(1, (aoaRatio - b.aoaStart) / (b.aoaFull - b.aoaStart)));
  }

  /**
   * Robust "resting on the ground" test for input gating (e.g. blocking gear
   * changes). The per-frame `onGround` contact flag is unreliable once the
   * airframe settles: the kinematic rest-pin parks the deepest wheel at exactly
   * zero penetration, and the strict (> 0) contact test then reads false on the
   * very next frame — the flag alternates once the aircraft is standing still.
   *
   * So OR in the two states that don't chatter, one per resting mode:
   *   - wheels down: the per-leg flags, which carry GROUND.wheelContactTolerance
   *     precisely because the rest height sits on the boundary;
   *   - gear up: AGL, since no wheel rays are cast at all and the belly rests
   *     about bellyHeight below the CG (same idiom as the wreck ground-fire
   *     test). This term cannot cover the gear-down case — the CG stands ~2.7 m
   *     up on the struts, well clear of any belly-scale threshold.
   */
  get isGrounded() {
    if (this.onGround) return true;
    const wg = this.wheelGround;
    if (wg.nose.onGround || wg.left.onGround || wg.right.onGround) return true;
    return isFinite(this.altitudeAGL) && this.altitudeAGL < GROUND.bellyHeight + 1.0;
  }
}
